import { Flag, CalendarDays, Route, Gauge, Compass } from "lucide-react";
import { ParentSignalTile, type AccentKey } from "@/components/parent/shared/parentPrimitives";
import type { CommandStatus } from "@/lib/parent/commandMetrics";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import type { ParentPriority } from "@/lib/parent/currentPriority";

interface Props {
  analytics: ParentAnalyticsResult;
  moduleReadiness: { percent: number; status: string };
  status: CommandStatus;
  priority: ParentPriority;
}

export default function KpiStrip({ analytics, moduleReadiness, status, priority }: Props) {
  // Phase 2 — single source of truth for X/13 (10 beta lessons + 3 beta modules).
  const total = analytics.betaPathCompleted;
  const activeDays = activeDaysThisWeek(analytics);
  const lastActive = formatRelative(analytics.lastActiveDate);
  const priorityChip =
    status.state === "reengage" ? "Re-engage" : status.state === "empty" ? "Start strong" : "Keep momentum";

  // Path tile now reflects the current parent priority (single source of truth).
  const readinessTone = moduleReadiness.percent >= 70 ? "ready" : moduleReadiness.percent >= 35 ? "growth" : "neutral";
  // Avoid a meaningless "0%" ring that reads like failure on a fresh learner.
  const readinessValue = moduleReadiness.percent > 0 ? `${moduleReadiness.percent}%` : "Forming";
  const readinessHint = moduleReadiness.percent > 0
    ? moduleReadiness.status
    : "Sharper after first checkpoint";
  const priorityTone = status.state === "reengage" ? "watch" : status.state === "empty" ? "growth" : "ready";
  const priorityAccent: AccentKey = status.state === "reengage" ? "amber" : status.state === "empty" ? "amber" : "emerald";
  const readinessAccent: AccentKey = moduleReadiness.percent >= 70 ? "emerald" : moduleReadiness.percent >= 35 ? "amber" : "neutral";
  const activeAccent: AccentKey = activeDays >= 3 ? "emerald" : activeDays >= 1 ? "cyan" : "neutral";

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
      <ParentSignalTile
        icon={Flag}
        label="Learning Path"
        value={<span className="block">{total}/{analytics.betaPathTotal}</span>}
        hint={total === 0 ? "Starts on first lesson" : "Learning moments"}
        accent="cyan"
      />
      <ParentSignalTile
        icon={CalendarDays}
        label="Active Days"
        value={<span className="block">{activeDays}</span>}
        hint={`Last active ${lastActive}`}
        accent={activeAccent}
      />
      <ParentSignalTile
        icon={Route}
        label="Focus"
        value={<span className="block text-[15px] leading-snug sm:text-[16px]">{priority.shortLabel}</span>}
        hint={priority.moduleLabel}
        accent="cyan"
      />
      <ParentSignalTile
        icon={Gauge}
        label="Readiness"
        value={<span className="block">{readinessValue}</span>}
        hint={readinessHint}
        tone={readinessTone}
        accent={readinessAccent}
      />
      <ParentSignalTile
        icon={Compass}
        label="Priority"
        value={<span className="block text-[15px] leading-snug sm:text-[16px]">{priorityChip}</span>}
        hint="Suggested"
        tone={priorityTone}
        accent={priorityAccent}
      />
    </div>
  );
}

function activeDaysThisWeek(a: ParentAnalyticsResult): number {
  // Phase 2 — UTC bucketing so the count is consistent with every other date
  // in the analytics layer (which all use UTC).
  const now = new Date();
  const dow = (now.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  const mondayUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - dow * 86400000;
  const days = new Set<string>();
  const toUtcDay = (iso: string) => {
    const d = new Date(iso);
    return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
  };
  for (const ev of a.lessonActivity) {
    if (new Date(ev.date).getTime() >= mondayUtcMs) days.add(toUtcDay(ev.date));
  }
  for (const ev of a.quizAttempts) {
    if (new Date(ev.completedAt).getTime() >= mondayUtcMs) days.add(toUtcDay(ev.completedAt));
  }
  return days.size;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}